import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown } from "lucide-react";
import { LANGUAGES } from "@/lib/i18n";

export default function LanguageSelector({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) || LANGUAGES[0];

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const change = (code) => { i18n.changeLanguage(code); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="language-selector-toggle"
        aria-label={t("language_selector.current_aria", { name: current.label })}
        className={`inline-flex items-center gap-1.5 rounded-sm border border-primary/40 bg-black/50 hover:bg-primary/10 text-primary font-mono-alt tracking-widest text-[10px] px-2.5 py-2 transition-colors ${compact ? "" : ""}`}
      >
        {compact ? <Globe className="h-3.5 w-3.5" /> : <span className="text-base leading-none">{current.flag}</span>}
        <span className="uppercase">{current.code}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="language-selector-menu"
          className="absolute right-0 top-full mt-1 min-w-[160px] rounded-sm border border-primary/40 bg-black/95 backdrop-blur-md shadow-xl shadow-primary/10 z-50 overflow-hidden"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => change(l.code)}
              data-testid={`language-option-${l.code}`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left font-mono-alt text-[11px] tracking-widest transition-colors ${
                l.code === current.code
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:bg-primary/10 hover:text-primary"
              }`}
            >
              <span className="text-base leading-none">{l.flag}</span>
              <span className="flex-1">{l.label}</span>
              {l.code === current.code && <span className="text-primary text-xs">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
